import { useApi, useApiLoader } from "@/services/api";
import { Field, FieldType } from "@/services/api/sdk";
import { computed, getCurrentInstance, onMounted } from "vue";

let cached: Field[] | null = null;
let loadingPromise: Promise<void> | null = null;

function getTypescriptType(field: Field) {
    switch (field.type) {
        case FieldType.Number: return 'number';
        case FieldType.Checkbox: return 'boolean';
        case FieldType.DateTime: return 'Date';
        case FieldType.Option: {
            return `${field.options!.map(x => `'${x}'`).join(' | ')}`;
        };
        case FieldType.Text:
        case FieldType.Date:
        case FieldType.Phone:
        case FieldType.Search:
            return 'string';
        default: return 'any';
    }
}

export function useFields() {
    const api = useApi();

    const { data: fields, loading, load } = useApiLoader(async () => {
        if (loadingPromise) {
            await loadingPromise;
        }
        if (!cached) {
            loadingPromise = new Promise(async (resolve) => {
                const res = await api.getFields();
                cached = res.data!;
                resolve();
            });
            await loadingPromise;
        }
        return cached!;
    }, []);

    const typescriptType = computed(() => {
        const body = fields.value.map(x => {
            return `    ${x.slug}: ${getTypescriptType(x)};`;
        });

        return `{
${body.join('\n')}
}`;
    });

    // Only register onMounted if there's an active component instance.
    // This allows the composable to be safely called outside of setup() context
    // (e.g., after awaits in lifecycle hooks).
    if (getCurrentInstance()) {
        onMounted(() => {
            load();
        });
    } else {
        // If called outside component context, load immediately
        load();
    }

    function invalidate() {
        cached = null;
    }

    return { fields, loading, invalidate, typescriptType };
}
